import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  limit,
  startAfter,
  getCountFromServer,
  Timestamp,
  deleteField,
  type UpdateData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { FirestorePageResult } from '@/hooks/useFirestorePagination'
import { db, COLLECTIONS } from '@/lib/firebase'
import {
  getDocumentCacheFirst,
  getQueryDocsCacheFirst,
} from '@/lib/firestoreCache'
import {
  buildProductSearchFields,
  parseSearchQuery,
  productMatchesSearchTokens,
} from '@/lib/productSearchFields'
import type { Product } from '@/types'
import { parseProductAudience } from '@/lib/productAudience'

const col = collection(db, COLLECTIONS.products)

function omitUndefinedFields<T extends Record<string, unknown>>(
  data: T
): UpdateData<T> {
  return Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== undefined)
  ) as UpdateData<T>
}

function sortProductsNewestFirst(products: Product[]): Product[] {
  return [...products].sort(
    (a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )
}

/** In-memory pagination cursor when composite Firestore indexes are unavailable. */
function productPageCursor(product: Product): QueryDocumentSnapshot {
  return { id: product.id } as QueryDocumentSnapshot
}

function activeProductsPageFromCatalog(
  products: Product[],
  opts: { pageSize: number; startAfter: QueryDocumentSnapshot | null }
): FirestorePageResult<Product> {
  const sorted = sortProductsNewestFirst(products)
  let startIndex = 0
  if (opts.startAfter) {
    const idx = sorted.findIndex((p) => p.id === opts.startAfter!.id)
    startIndex = idx >= 0 ? idx + 1 : 0
  }
  const items = sorted.slice(startIndex, startIndex + opts.pageSize)
  const lastItem = items.length > 0 ? items[items.length - 1] : null
  return {
    items,
    lastDoc: lastItem ? productPageCursor(lastItem) : null,
    hasMore: startIndex + opts.pageSize < sorted.length,
  }
}

function mapProduct(id: string, data: Record<string, unknown>): Product {
  return {
    id,
    name: (data.name as string) || '',
    subtitle: data.subtitle as string | undefined,
    description: (data.description as string) || '',
    fragranceNotes: data.fragranceNotes as string | undefined,
    category: (data.category as string) || 'Attar',
    price: Number(data.price) || 0,
    discountPrice: data.discountPrice ? Number(data.discountPrice) : undefined,
    quantity: Number(data.quantity) || 0,
    status: (data.status as Product['status']) || 'active',
    sku: (data.sku as string) || '',
    images: (data.images as string[]) || [],
    sizes: data.sizes as Product['sizes'],
    featured: Boolean(data.featured),
    audience: parseProductAudience(data.audience),
    lowStockThreshold: Number(data.lowStockThreshold) || 5,
    averageRating:
      data.averageRating != null && Number.isFinite(Number(data.averageRating))
        ? Number(data.averageRating)
        : undefined,
    reviewCount:
      data.reviewCount != null && Number(data.reviewCount) > 0
        ? Number(data.reviewCount)
        : undefined,
    createdAt: (data.createdAt as string) || new Date().toISOString(),
    updatedAt: (data.updatedAt as string) || new Date().toISOString(),
  }
}

export type ProductCategoryFilter = string

function buildProductsQuery(category: ProductCategoryFilter = 'all') {
  if (category !== 'all') {
    return query(
      col,
      where('category', '==', category),
      orderBy('createdAt', 'desc')
    )
  }
  return query(col, orderBy('createdAt', 'desc'))
}

export async function countProducts(
  category: ProductCategoryFilter = 'all'
): Promise<number> {
  const snap = await getCountFromServer(buildProductsQuery(category))
  return snap.data().count
}

export async function fetchProductsPage(opts: {
  pageSize: number
  startAfter: QueryDocumentSnapshot | null
  category?: ProductCategoryFilter
}): Promise<FirestorePageResult<Product>> {
  const base = buildProductsQuery(opts.category ?? 'all')
  const q =
    opts.startAfter != null
      ? query(base, startAfter(opts.startAfter), limit(opts.pageSize))
      : query(base, limit(opts.pageSize))
  const snap = await getDocs(q)
  const items = snap.docs.map((d) => mapProduct(d.id, d.data()))
  const lastDoc =
    snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null
  return {
    items,
    lastDoc,
    hasMore: snap.docs.length === opts.pageSize,
  }
}

export async function fetchProducts(): Promise<Product[]> {
  const snap = await getDocs(buildProductsQuery('all'))
  return snap.docs.map((d) => mapProduct(d.id, d.data()))
}

export async function fetchStoreProducts(): Promise<Product[]> {
  try {
    const docs = await getQueryDocsCacheFirst(activeStoreProductsQuery())
    return docs.map((d) => mapProduct(d.id, d.data()))
  } catch {
    try {
      const docs = await getQueryDocsCacheFirst(
        query(col, where('status', '==', 'active'))
      )
      return sortProductsNewestFirst(
        docs.map((d) => mapProduct(d.id, d.data()))
      )
    } catch {
      return []
    }
  }
}

export async function fetchFeaturedStoreProducts(maxItems = 4): Promise<Product[]> {
  const cap = Math.max(1, maxItems)
  try {
    const docs = await getQueryDocsCacheFirst(
      query(
        col,
        where('status', '==', 'active'),
        where('featured', '==', true),
        orderBy('createdAt', 'desc'),
        limit(cap)
      )
    )
    return docs.map((d) => mapProduct(d.id, d.data()))
  } catch {
    return sortProductsNewestFirst(await fetchStoreProducts())
      .filter((p) => p.featured)
      .slice(0, cap)
  }
}

export async function fetchNewArrivalStoreProducts(maxItems = 4): Promise<Product[]> {
  const cap = Math.max(1, maxItems)
  try {
    const docs = await getQueryDocsCacheFirst(
      query(activeStoreProductsQuery(), limit(cap))
    )
    return docs.map((d) => mapProduct(d.id, d.data()))
  } catch {
    return sortProductsNewestFirst(await fetchStoreProducts()).slice(0, cap)
  }
}

/** Same-category storefront products for the detail page (no full-catalog fetch). */
export async function fetchSimilarStoreProducts(
  category: string,
  excludeId: string,
  maxItems = 4
): Promise<Product[]> {
  const cap = Math.max(maxItems + 2, 6)
  try {
    const q = query(
      col,
      where('status', '==', 'active'),
      where('category', '==', category),
      orderBy('createdAt', 'desc'),
      limit(cap)
    )
    const snap = await getDocs(q)
    return snap.docs
      .map((d) => mapProduct(d.id, d.data()))
      .filter((p) => p.status === 'active' && p.id !== excludeId)
      .slice(0, maxItems)
  } catch {
    return sortProductsNewestFirst(await fetchStoreProducts())
      .filter((p) => p.category === category && p.id !== excludeId)
      .slice(0, maxItems)
  }
}

export async function fetchStoreProductsByIds(ids: string[]): Promise<Product[]> {
  const unique = [...new Set(ids.filter(Boolean))]
  if (unique.length === 0) return []

  const batches: string[][] = []
  for (let i = 0; i < unique.length; i += 30) {
    batches.push(unique.slice(i, i + 30))
  }

  const maps = await Promise.all(
    batches.map(async (batch) => {
      const items = await Promise.all(
        batch.map(async (id) => {
          try {
            const snap = await getDocumentCacheFirst(doc(db, COLLECTIONS.products, id))
            if (!snap.exists()) return null
            const product = mapProduct(snap.id, snap.data())
            return product.status === 'active' ? product : null
          } catch {
            return null
          }
        })
      )
      return items.filter((p): p is Product => p != null)
    })
  )

  const byId = new Map(maps.flat().map((p) => [p.id, p]))
  return unique
    .map((id) => byId.get(id))
    .filter((p): p is Product => Boolean(p))
}

function activeStoreProductsQuery() {
  return query(
    col,
    where('status', '==', 'active'),
    orderBy('createdAt', 'desc')
  )
}

function activeStoreProductsSearchQuery(searchTokens: string[]) {
  const base = query(col, where('status', '==', 'active'))
  if (searchTokens.length === 1) {
    return query(
      base,
      where('searchTokens', 'array-contains', searchTokens[0]),
      orderBy('createdAt', 'desc')
    )
  }
  return query(
    base,
    where('searchTokens', 'array-contains-any', searchTokens.slice(0, 10)),
    orderBy('createdAt', 'desc')
  )
}

export async function countActiveStoreProducts(): Promise<number> {
  try {
    const snap = await getCountFromServer(activeStoreProductsQuery())
    return snap.data().count
  } catch {
    return (await fetchStoreProducts()).length
  }
}

export async function countActiveStoreProductsSearch(q: string): Promise<number> {
  const tokens = parseSearchQuery(q)
  if (tokens.length === 0) return 0
  const snap = await getCountFromServer(activeStoreProductsSearchQuery(tokens))
  return snap.data().count
}

export async function fetchActiveStoreProductsPage(opts: {
  pageSize: number
  startAfter: QueryDocumentSnapshot | null
}): Promise<FirestorePageResult<Product>> {
  try {
    const base = activeStoreProductsQuery()
    const q =
      opts.startAfter != null
        ? query(base, startAfter(opts.startAfter), limit(opts.pageSize))
        : query(base, limit(opts.pageSize))
    const snap = await getDocs(q)
    const items = snap.docs.map((d) => mapProduct(d.id, d.data()))
    const lastDoc =
      snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null
    return {
      items,
      lastDoc,
      hasMore: snap.docs.length === opts.pageSize,
    }
  } catch {
    return activeProductsPageFromCatalog(await fetchStoreProducts(), opts)
  }
}

export async function fetchActiveStoreProductsSearchPage(
  q: string,
  opts: {
    pageSize: number
    startAfter: QueryDocumentSnapshot | null
  }
): Promise<FirestorePageResult<Product>> {
  const tokens = parseSearchQuery(q)
  if (tokens.length === 0) {
    return { items: [], lastDoc: null, hasMore: false }
  }

  const base = activeStoreProductsSearchQuery(tokens)
  const queryRef =
    opts.startAfter != null
      ? query(base, startAfter(opts.startAfter), limit(opts.pageSize))
      : query(base, limit(opts.pageSize))
  const snap = await getDocs(queryRef)
  const items = snap.docs.map((d) => mapProduct(d.id, d.data()))
  const lastDoc =
    snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null
  return {
    items,
    lastDoc,
    hasMore: snap.docs.length === opts.pageSize,
  }
}

/** Search active storefront products (client-side filter; works without searchTokens). */
export async function searchActiveStoreProducts(q: string): Promise<Product[]> {
  const tokens = parseSearchQuery(q)
  if (tokens.length === 0) return []

  let products: Product[] = []
  try {
    const snap = await getDocs(activeStoreProductsSearchQuery(tokens))
    products = snap.docs.map((d) => mapProduct(d.id, d.data()))
    products = products.filter((p) => productMatchesSearchTokens(p, tokens))
  } catch {
    products = []
  }

  if (products.length === 0) {
    try {
      const snap = await getDocs(activeStoreProductsQuery())
      products = snap.docs
        .map((d) => mapProduct(d.id, d.data()))
        .filter((p) => productMatchesSearchTokens(p, tokens))
    } catch {
      products = (await fetchStoreProducts()).filter((p) =>
        productMatchesSearchTokens(p, tokens)
      )
    }
  }

  return sortProductsNewestFirst(products)
}

export async function fetchProduct(id: string): Promise<Product | null> {
  try {
    const snap = await getDoc(doc(db, COLLECTIONS.products, id))
    if (!snap.exists()) return null
    return mapProduct(snap.id, snap.data())
  } catch {
    return null
  }
}

export async function createProduct(
  data: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> {
  const now = new Date().toISOString()
  const { audience, ...rest } = data
  const ref = await addDoc(col, {
    ...rest,
    ...(audience ? { audience } : {}),
    ...buildProductSearchFields(data),
    createdAt: now,
    updatedAt: now,
    createdAtTs: Timestamp.now(),
  })
  return ref.id
}

export async function updateProduct(
  id: string,
  data: Partial<Product>
): Promise<void> {
  const {
    id: _id,
    createdAt: _createdAt,
    audience,
    averageRating: _averageRating,
    reviewCount: _reviewCount,
    ...rest
  } = data
  let searchFields: ReturnType<typeof buildProductSearchFields> | undefined

  const textFields = [
    'name',
    'subtitle',
    'description',
    'fragranceNotes',
    'category',
    'sku',
  ] as const
  if (textFields.some((field) => field in rest)) {
    const existing = await fetchProduct(id)
    if (existing) {
      searchFields = buildProductSearchFields({ ...existing, ...rest })
    }
  }

  const audienceUpdate =
    'audience' in data
      ? audience
        ? { audience }
        : { audience: deleteField() }
      : {}

  await updateDoc(
    doc(db, COLLECTIONS.products, id),
    omitUndefinedFields({
      ...rest,
      ...searchFields,
      ...audienceUpdate,
      updatedAt: new Date().toISOString(),
    })
  )
}

export async function deleteProduct(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.products, id))
}
